import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Lock, CreditCard, UserPlus, CheckCircle2, Wrench } from "lucide-react";

const KIND_ICON = {
  payment_pending: CreditCard, approval_pending: Lock, assign_owner: UserPlus,
  closed: CheckCircle2, workflow_setup_required: Wrench,
};

/**
 * THE canonical Next Action display (Workstream B / B3). Renders exactly
 * what the server derived LIVE from the workflow registry + the Case's real
 * prerequisite state (process_workflow.compute_next_action) — title,
 * responsible employee, due/reminder, the specific blocker, and ONE action
 * button that goes straight to resolving it. Never invents a transition:
 * a payment-pending Case shows the payment action, never "progress".
 *
 * `onOpenOutcome(taskId|null)` — when rendered on the Case page itself the
 * outcome form opens in place; elsewhere (Customer 360) the button
 * navigates to the Case with that exact task's form open.
 */
export function NextActionCard({ action, onOpenOutcome, onAssignOwner, onChanged, compact, testId }) {
  const navigate = useNavigate();
  if (!action || action.kind === "legacy") return null;
  const Icon = KIND_ICON[action.kind] || ChevronRight;
  const a = action.action || {};

  const go = async () => {
    if (a.type === "open_outcome") {
      if (onOpenOutcome) {
        if (a.task_id) { onOpenOutcome(a.task_id); return; }
        try {
          const { data } = await api.post(`/cases/${action.case_id}/workflow/open-outcome`, {});
          onOpenOutcome(data.task_id);
        } catch (e) { toast.error(errDetail(e)); }
        return;
      }
      navigate(a.route || `/process/${action.case_id}`);
      return;
    }
    if (a.type === "assign_owner" && onAssignOwner) { onAssignOwner(); return; }
    if (a.type === "reconcile") {
      try {
        await api.post(`/cases/${action.case_id}/workflow/reconcile`);
        toast.success("Advanced"); onChanged?.();
      } catch (e) { toast.error(errDetail(e)); }
      return;
    }
    if (a.route) navigate(a.route);
  };

  const blocked = !!action.blocker;
  return (
    <div data-testid={testId || "next-action"}
      className={`rounded-md border px-3 py-2 flex items-start gap-2 text-sm ${
        blocked ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" : "bg-[hsl(var(--accent))]/50"}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase font-semibold text-muted-foreground">Next action</span>
          <span className="font-medium" data-testid={`${testId || "next-action"}-title`}>{action.title}</span>
          {action.is_overdue && <Badge variant="outline" className="text-[9px] bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]">Overdue</Badge>}
        </div>
        {!compact && (
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
            {action.stage && <span>Stage: <b>{action.stage}</b></span>}
            <span>Responsible: <b>{action.responsible_name || "Unassigned"}</b></span>
            {action.due_at && <span>Due {fmtIST(action.due_at)}</span>}
            {action.reminder_at && <span>Reminder {fmtIST(action.reminder_at)}</span>}
          </div>
        )}
        {blocked && (
          <div className="text-xs" data-testid={`${testId || "next-action"}-blocker`}>
            <b>Blocked:</b> {action.blocker.message}
          </div>
        )}
      </div>
      {a.type && a.type !== "none" && (
        <Button size="sm" className="h-7 text-xs shrink-0" onClick={go}
          data-testid={`${testId || "next-action"}-button`}>
          {a.label || "Open"}
        </Button>
      )}
    </div>
  );
}
